import React, { useState, useEffect, useCallback } from 'react';
import { Users, CalendarCheck, ShieldAlert, NotebookPen, BookOpen, FileText, Lock } from 'lucide-react';
import { Gate } from '../AccessStudio';
import { usePermissions } from '../../contexts/PermissionContext';
import RosterView from './roster/RosterView';
import AttendanceSheet from './attendance/AttendanceSheet';
import BehaviorLog from './behavior/BehaviorLog';
import AgendaBoard from './agenda/AgendaBoard';
import GradingView from './grading/GradingView';
import ReportsView from './reports/ReportsView';

// ============================================================
// Academic & Student Management — module shell.
// Each tab's real content lives in its own subfolder (roster/,
// attendance/, ...) so features stay decoupled.
// ============================================================

// Teachers / surveillants / principals are pinned to the active year — no
// year picker, no historical browsing. Admins keep full year access.
const YEAR_LOCKED_ROLES = ['Teacher', 'Surveillant', 'Principal'];

const TAB_LABELS = {
  roster: 'Roster', attendance: 'Attendance', behavior: 'Behavior',
  agenda: 'Agenda', grading: 'Grading', reports: 'Reports'
};

const tabButton = (id, Icon, activeTab, setActiveTab) => (
  <button
    onClick={() => setActiveTab(id)}
    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
      activeTab === id ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon size={16} /> {TAB_LABELS[id]}
  </button>
);

const Academic = ({ apiCall, selectedYear, hasPermission, showSuccess, showError }) => {
  const [activeTab, setActiveTab] = useState('roster');
  const [years, setYears] = useState([]);
  // The academic year being worked on; defaults to the system's current year
  const [year, setYear] = useState(selectedYear);

  const { roles, isGlobalAdmin } = usePermissions();
  // Lock only PURE academic accounts; a user who also holds a financial role
  // (Afterschool/Registration/Ifada/…) keeps year switching. Admins too.
  const lockYear = !isGlobalAdmin && roles.length > 0 && roles.every(r => YEAR_LOCKED_ROLES.includes(r));
  // Year-locked roles always work in the active year — keep it pinned.
  useEffect(() => { if (lockYear) setYear(selectedYear); }, [lockYear, selectedYear]);

  const loadYears = useCallback(async () => {
    try {
      const data = await apiCall('/system/academic-years');
      const list = data.map(y => y.AcademicYear);
      // make sure next year is offerable even before it has data
      if (!list.includes('26-27')) list.unshift('26-27');
      setYears(list);
    } catch (err) {
      console.error('Failed to load academic years:', err);
      setYears([selectedYear]);
    }
  }, [apiCall, selectedYear]);

  useEffect(() => { loadYears(); }, [loadYears]);

  // Historical years are locked for writes (backend enforces; this is UX)
  const isLockedYear = year !== selectedYear && year < selectedYear;

  return (
    <div>
      {/* Header: tabs + year picker */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {/* Literal Gates so scan-gates.js keeps the catalog in sync */}
          <Gate permission="academic.roster.view" label="Roster tab">
            {tabButton('roster', Users, activeTab, setActiveTab)}
          </Gate>
          <Gate permission="academic.attendance.view" label="Attendance tab">
            {tabButton('attendance', CalendarCheck, activeTab, setActiveTab)}
          </Gate>
          <Gate permission="academic.behavior.view" label="Behavior tab">
            {tabButton('behavior', ShieldAlert, activeTab, setActiveTab)}
          </Gate>
          <Gate permission="academic.agenda.view" label="Agenda tab">
            {tabButton('agenda', NotebookPen, activeTab, setActiveTab)}
          </Gate>
          <Gate permission="academic.grading.view" label="Grading tab">
            {tabButton('grading', BookOpen, activeTab, setActiveTab)}
          </Gate>
          <Gate permission="academic.reports.view" label="Academic Reports tab">
            {tabButton('reports', FileText, activeTab, setActiveTab)}
          </Gate>
        </div>
        <div className="flex items-center gap-2">
          {lockYear ? (
            // Year-locked roles: show the active year as a static label, no picker.
            <span className="text-sm font-semibold text-gray-800 border rounded-lg px-3 py-2 bg-gray-50">{year}</span>
          ) : (
            <>
              {isLockedYear && (
                <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                  <Lock size={12} /> locked year — admin only
                </span>
              )}
              <select
                value={year}
                onChange={e => setYear(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {activeTab === 'roster' && (
        <RosterView apiCall={apiCall} year={year} hasPermission={hasPermission}
          showSuccess={showSuccess} showError={showError} />
      )}
      {activeTab === 'attendance' && (
        <AttendanceSheet apiCall={apiCall} year={year} showSuccess={showSuccess} showError={showError} />
      )}
      {activeTab === 'behavior' && (
        <BehaviorLog apiCall={apiCall} year={year} showSuccess={showSuccess} showError={showError} />
      )}
      {activeTab === 'agenda' && (
        <AgendaBoard apiCall={apiCall} year={year} hasPermission={hasPermission}
          showSuccess={showSuccess} showError={showError} />
      )}
      {activeTab === 'grading' && (
        <GradingView apiCall={apiCall} year={year} showSuccess={showSuccess} showError={showError} />
      )}
      {activeTab === 'reports' && (
        <ReportsView apiCall={apiCall} year={year} showError={showError} showSuccess={showSuccess} />
      )}
    </div>
  );
};

export default Academic;
